import { useState } from 'react';
import api from '../../hooks/useApi';
import { fmtDateTimeLong } from '../../utils/dates.js';

const fmt = (v) => v != null ? `£${Number(v).toFixed(2)}` : '—';

// The OneMedia lookup on the Product Catalog edit screen. OneMedia have no
// per-item API, so this searches the mirror of their product feed (pulled every
// six hours) through the shared distributor search, which takes a part number,
// a OneMedia code or free text. Prices say which feed they came from rather
// than reading as live.
//
// "Use" fills the add-supplier form; "Update product" proposes the product's
// fields and adds the OneMedia price in one go. Either way the supplier SKU is
// the OneMedia code (sf_code), which is what the feed sync keys its price
// updates on.
export default function OneMediaLookup({ editing, onUse, onApply }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [toggles, setToggles] = useState({});

  const search = async () => {
    const q = term.trim();
    if (!q) return;
    setSearching(true);
    setResults([]);
    setDone(false);
    setError(null);
    setCandidate(null);
    try {
      const res = await api.get('distributors/search', { q, supplier: 'OneMedia', limit: 20 });
      setResults(res.products || []);
      // An unconfigured feed or a too-short term comes back as an errors[] entry
      // alongside an empty list, not as a failed request.
      if (res.errors?.length) setError(res.errors[0].error);
    } catch (e) {
      setError(e?.data?.error || 'OneMedia search failed');
    } finally {
      setSearching(false);
      setDone(true);
    }
  };

  const supplierFrom = (r) => ({
    supplier_name: 'OneMedia',
    supplier_sku: r.supplier_part_number,
    unit_price: r.trade_price != null ? String(r.trade_price) : '',
    auto_sync: true,
    notes: [r.part_number, r.description].filter(Boolean).join(' — ').slice(0, 100),
  });

  const propose = (r) => {
    const fields = {
      description: { label: 'Description', current: editing.description, proposed: r.description || '' },
      manufacturer: { label: 'Manufacturer', current: editing.manufacturer, proposed: r.manufacturer || '' },
      manufacturer_part_number: { label: 'Manuf Part No.', current: editing.manufacturer_part_number, proposed: r.part_number || '' },
      category: { label: 'Category', current: editing.category, proposed: r.category || '' },
    };
    setCandidate({ result: r, fields });
    setToggles(Object.fromEntries(
      Object.entries(fields).map(([k, v]) => [k, !!(v.proposed && v.proposed !== v.current)])
    ));
  };

  const apply = async () => {
    const updates = {};
    for (const [key, v] of Object.entries(candidate.fields)) {
      if (toggles[key] && v.proposed) updates[key] = v.proposed;
    }
    await onApply(updates, supplierFrom(candidate.result));
    setCandidate(null);
  };

  const asAt = results.find(r => r.price_as_at)?.price_as_at;

  return (
    <div className="border border-violet-200 rounded p-2 bg-violet-50 space-y-2 mb-2" data-testid="onemedia-lookup">
      <div className="flex gap-2">
        <input
          type="text"
          className="form-input flex-1 text-[9px]"
          placeholder="Part number, OneMedia code or description..."
          value={term}
          onChange={e => setTerm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
        />
        <button className="btn-primary text-[9px] px-2 py-1" onClick={search} disabled={searching}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>
      <div className="text-[7px] text-violet-700">
        {asAt
          ? `From OneMedia's product feed of ${fmtDateTimeLong(asAt)}.`
          : 'From the OneMedia product feed, refreshed every six hours.'}
      </div>
      {searching && (
        <div className="flex items-center gap-2 py-2 text-[9px] text-violet-700">
          <svg className="animate-spin w-3 h-3 text-violet-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          Searching the OneMedia product feed…
        </div>
      )}
      {error && !searching && (
        <div className="text-[9px] text-red-700">{error}</div>
      )}
      {done && !searching && !error && results.length === 0 && (
        <div className="text-center py-2 text-[9px] text-gray-400">Nothing in the latest OneMedia feed matches "{term}"</div>
      )}
      {results.length > 0 && (
        <div className="space-y-1.5">
          {results.map((r) => (
            <div key={r.supplier_part_number} className="border border-violet-100 rounded p-2 bg-white">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-[9px] text-navy">{r.part_number || '—'}</span>
                    {r.supplier_part_number && r.supplier_part_number !== r.part_number && (
                      <span className="text-[7px] text-gray-400 font-mono">OM: {r.supplier_part_number}</span>
                    )}
                  </div>
                  {r.description && <div className="text-[8px] text-gray-800 mt-0.5 font-medium">{r.description}</div>}
                  <div className="text-[7px] text-gray-400 mt-0.5 flex gap-2 flex-wrap">
                    {r.manufacturer && <span>{r.manufacturer}</span>}
                    {r.category && <span>· {r.category}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {r.trade_price != null
                    ? <div className="text-[11px] font-extrabold text-emerald-700">{fmt(r.trade_price)}</div>
                    : <div className="text-[9px] text-gray-400">price on request</div>}
                  {r.list_price != null && <div className="text-[7px] text-gray-400">List {fmt(r.list_price)}</div>}
                  <div className={`text-[7px] mt-0.5 ${Number(r.stock) > 0 ? 'text-gray-500' : 'text-amber-600'}`}>
                    {Number(r.stock) > 0 ? `${r.stock} available` : 'none available'}
                  </div>
                  <div className="flex gap-2 justify-end mt-1">
                    <button className="text-[8px] font-semibold text-emerald-700 underline" onClick={() => propose(r)}>Update product</button>
                    <button className="text-[8px] font-semibold text-blue-600 underline" onClick={() => onUse(supplierFrom(r))}>Use</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {candidate && (
        <div className="border border-emerald-200 bg-emerald-50 rounded p-2 space-y-1.5">
          <div className="text-[8px] font-bold text-emerald-800">Update product fields from OneMedia?</div>
          {Object.entries(candidate.fields).filter(([, v]) => v.proposed).map(([key, v]) => (
            <div key={key} className="flex items-start gap-2">
              <button
                type="button"
                className={`mt-0.5 w-7 h-3.5 rounded-full flex-shrink-0 transition-colors ${toggles[key] ? 'bg-emerald-500' : 'bg-gray-300'}`}
                onClick={() => setToggles(t => ({ ...t, [key]: !t[key] }))}
              >
                <div className={`w-3 h-3 rounded-full bg-white shadow mx-0.5 transition-transform ${toggles[key] ? 'translate-x-3' : ''}`} />
              </button>
              <div className="flex-1 min-w-0 text-[7.5px]">
                <div className="font-bold text-gray-500 uppercase">{v.label}</div>
                <div className="text-gray-400 truncate">
                  <span className="line-through">{v.current || '—'}</span>
                  <span className="mx-1 text-gray-300">→</span>
                  <span className={toggles[key] ? 'text-emerald-700 font-semibold' : 'text-gray-500'}>{v.proposed}</span>
                </div>
              </div>
            </div>
          ))}
          <div className="flex gap-2 pt-0.5">
            <button className="text-[8px] px-2 py-0.5 bg-emerald-600 text-white rounded font-semibold" onClick={apply}>Apply selected</button>
            <button className="text-[8px] px-2 py-0.5 border border-gray-300 rounded text-gray-500" onClick={() => setCandidate(null)}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}
